/**
 * Blackjack bot.
 *
 * - Betting: a modest multiple of the minimum bet, scaled by difficulty and capped by its stack.
 * - Play: basic strategy for a multi-deck shoe (hit, stand, double, split by the player's hand against the dealer's
 *   upcard). Beginner and easy bots sometimes play a hand the way a casual player would (stand on 12+ / copy the dealer).
 * The bot only ever reads its own seat's view.
 */
import { parseCard } from '../../core/cards.js';
import { chance, type BotEngine } from '../../bots/types.js';
import { blackjackUpValue, blackjackValue, type BlackjackAction, type BlackjackLegal, type BlackjackView } from './engine.js';

type Move = 'hit' | 'stand' | 'double' | 'split';

/** Basic-strategy decision (before checking what is allowed). `up` is the dealer upcard value, ace = 11. */
export function basicStrategy(cards: readonly string[], up: number, canSplit: boolean): Move {
  const { total, soft } = blackjackValue(cards);
  const pairRank = cards.length === 2 && parseCard(cards[0]!).rank === parseCard(cards[1]!).rank ? Math.min(parseCard(cards[0]!).rank, 10) : 0;
  const aceP = cards.length === 2 && parseCard(cards[0]!).rank === 14 && parseCard(cards[1]!).rank === 14;
  if (canSplit && (pairRank || aceP)) {
    const r = aceP ? 11 : pairRank;
    if (r === 11 || r === 8) return 'split';
    if (r === 9 && up !== 7 && up <= 9) return 'split';
    if (r === 7 && up <= 7) return 'split';
    if (r === 6 && up <= 6) return 'split';
    if ((r === 3 || r === 2) && up <= 7) return 'split';
    if (r === 4 && (up === 5 || up === 6)) return 'split';
  }
  if (soft) {
    if (total >= 19) return 'stand';
    if (total === 18) return up >= 3 && up <= 6 ? 'double' : up <= 8 ? 'stand' : 'hit';
    if (total === 17) return up >= 3 && up <= 6 ? 'double' : 'hit';
    if (total === 15 || total === 16) return up >= 4 && up <= 6 ? 'double' : 'hit';
    return up >= 5 && up <= 6 ? 'double' : 'hit';
  }
  if (total >= 17) return 'stand';
  if (total >= 13) return up <= 6 ? 'stand' : 'hit';
  if (total === 12) return up >= 4 && up <= 6 ? 'stand' : 'hit';
  if (total === 11) return up === 11 ? 'hit' : 'double';
  if (total === 10) return up <= 9 ? 'double' : 'hit';
  if (total === 9) return up >= 3 && up <= 6 ? 'double' : 'hit';
  return 'hit';
}

/** Falls back to the nearest allowed move when the preferred one is not available. */
function allowed(move: Move, legal: Extract<BlackjackLegal, { kind: 'play' }>, cards: readonly string[]): Move {
  if (move === 'double' && !legal.double) {
    // Soft 18 doubles fall back to standing, everything else to hitting.
    const { total, soft } = blackjackValue(cards);
    return soft && total >= 18 ? 'stand' : 'hit';
  }
  return move;
}

export const blackjackBot: BotEngine<BlackjackView, BlackjackLegal, BlackjackAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bet') return this.chooseBet!(view, legal, ctx);
    return this.chooseCard!(view, legal, ctx);
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bet') return this.handleTimeout(view, legal, ctx);
    const max = ctx.difficulty === 'BEGINNER' ? 3 : ctx.difficulty === 'EASY' ? 3 : 2;
    const wanted = legal.min * (1 + ctx.rng.int(max));
    // Never risk more than a tenth of the stack on one hand.
    const me = view.seat === null ? null : view.players[view.seat];
    const cap = me ? Math.max(legal.min, Math.floor(me.chips / 10)) : legal.min;
    return { type: 'bet', amount: Math.max(legal.min, Math.min(wanted, cap, legal.max)) };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const me = view.players[view.seat!]!;
    const hand = me.hands[view.activeHand]!;
    const up = blackjackUpValue(view.dealer[0]!);
    const options = (['hit', 'stand', 'double', 'split'] as const).filter((m) => legal[m]);
    if (chance(ctx, ctx.profile.mistakeRate)) {
      // Casual play: stand on 12 or more, otherwise hit.
      const casual: Move = hand.total >= 12 ? 'stand' : 'hit';
      return { type: options.includes(casual) ? casual : options[0]! };
    }
    const move = allowed(basicStrategy(hand.cards, up, legal.split), legal, hand.cards);
    return { type: legal[move] ? move : options[0]! };
  },

  evaluateState(view) {
    const me = view.seat === null ? null : view.players[view.seat];
    return me ? Math.max(0, Math.min(1, me.chips / (view.config.minBet * 200))) : 0;
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(_view, legal) {
    if (legal.kind === 'bet') return { type: 'sitOut' };
    return { type: 'stand' };
  },
};
